/**
 * THE PAGE STRIPE SENDS HER BACK TO (ops #46), at `PAYMENT_RETURN_PATH`. No sign-in, no navigation,
 * and the pay page's own dress, so the page she comes back to looks like the page she left.
 *
 * Every page Stripe makes for this business names it twice. Once she has paid, Stripe sends her here
 * and the page thanks her: the payment is on its way to Faithfully Kept, and the office's number is
 * here. If she leaves Stripe's page by its back arrow without paying, Stripe sends her here with
 * `PAYMENT_NOT_PAID` in the query, and the page says she has not paid yet and how to pay. That address
 * was the website's home page before, with nothing on it to say the money had gone.
 *
 * WHAT IT SAYS FOLLOWS THE PAGE SHE PAID ON (the staging walk of 2026-09-23), which the address names
 * (`PAYMENT_RETURN_PAGE`, `PAYMENT_RETURN_SAVES`), as Stripe was asked to make it. After the bank page,
 * or the office's page of every way, which takes a bank payment too, that a bank payment can take a few
 * days, and that a bank account she typed in by hand is confirmed by small deposits Stripe emails her
 * about, Stripe sending her here before she has confirmed them, so the payment starts once she does.
 * After the card page, nothing of a bank payment, which is all the walk found it saying. After a bank
 * page her own tick asked to save the account she paid from, plainly that it is charged automatically
 * once this payment clears; and after a page offering Stripe's save box, what saving means, said as an
 * if, because only Stripe knows whether she ticked it. An address that names no page, a page made
 * before these, or a word the page does not know, says only what it said before.
 *
 * ITS ADDRESS HOLDS NO TOKEN, because Stripe keeps both addresses on every session it makes, so it
 * knows nothing about her: it asks the server nothing, and names no household, no invoice and no
 * amount. Each word is true for whoever lands here, and a bank payment still clearing, or one that
 * failed, is the invoice's own link to say and the office's to follow up, never this page's.
 *
 * App.tsx renders this BEFORE asking who is signed in, beside the link to her invoice.
 */
import { useSearchParams } from 'react-router-dom';
import {
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_E164,
  PAY_PAGE_KINDS,
  PAYMENT_NOT_PAID,
  PAYMENT_RETURN_PAGE,
  PAYMENT_RETURN_SAVES,
  type PayPageKind,
} from '@fk/shared';
import { PublicFrame } from '../components/PublicFrame.tsx';
import { RETURN_PAGE_WORDS as WORDS } from '../pay-words.ts';

/** The page she paid on, from the address, or null for one that names none it knows. */
function pageOf(query: URLSearchParams): PayPageKind | null {
  const word = query.get(PAYMENT_RETURN_PAGE);
  return word !== null && (PAY_PAGE_KINDS as readonly string[]).includes(word)
    ? (word as PayPageKind)
    : null;
}

export function PaymentReturn() {
  const [query] = useSearchParams();
  const notPaid = query.has(PAYMENT_NOT_PAID);
  const page = pageOf(query);
  const saves = query.get(PAYMENT_RETURN_SAVES);
  // A bank payment could have been made on every page but the card page's.
  const couldBeBank = page !== 'card';
  // Only a bank page saves the account she pays from, and only a card page or the office's page
  // offers Stripe's box.
  const savedAccount = page === 'bank' && saves === 'account';
  const offeredBox = (page === 'card' || page === 'all') && saves === 'box';

  return (
    <PublicFrame>
      <section className="rounded-xl border border-navy/12 bg-parchment p-5">
        {notPaid ? (
          <>
            <h1 className="font-serif text-2xl leading-tight">{WORDS.notPaid}</h1>
            <p className="mt-3 text-base">{WORDS.leftStripe}</p>
            <p className="mt-2 text-base">{WORDS.howToPay}</p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-2xl leading-tight">{WORDS.paid}</h1>
            <p className="mt-3 text-base">{WORDS.onItsWay}</p>
            {couldBeBank ? (
              <>
                <p className="mt-2 text-base">{WORDS.bankTakesDays}</p>
                <p className="mt-2 text-base">{WORDS.confirmYourBank}</p>
              </>
            ) : null}
            {savedAccount ? <p className="mt-2 text-base">{WORDS.savedAccount}</p> : null}
            {offeredBox ? <p className="mt-2 text-base">{WORDS.savedBox}</p> : null}
            <p className="mt-2 text-base">{WORDS.close}</p>
          </>
        )}
        <p className="mt-5 text-sm text-navy/70">
          {WORDS.questions}{' '}
          <a href={`tel:${BUSINESS_PHONE_E164}`} className="font-medium underline">
            {BUSINESS_PHONE_DISPLAY}
          </a>
          .
        </p>
      </section>
    </PublicFrame>
  );
}
